import { Middleware } from '../generated-api';

const XSRF_TOKEN_HEADER = 'X-XSRF-TOKEN';

function getXsrfCookieName(): string {
  const hostname = window.location.hostname;
  if (isKnowspreadEnvironment(hostname, 'dev')) return 'dev_XSRF-TOKEN';
  if (isKnowspreadEnvironment(hostname, 'staging')) return 'staging_XSRF-TOKEN';

  // The dev server is served from kws.localhost but proxies /api to a remote
  // environment (setupProxy.js), which names the cookie after itself. Reading
  // the environment off the browser host therefore always lands on the plain
  // name, and every CSRF-protected verb answers 422. Guarded to the dev server
  // so a production build never takes the committed .env value into account.
  if (process.env.NODE_ENV === 'development') {
    const proxied = proxyEnvironmentCookieName();
    if (proxied) return proxied;
  }

  return 'XSRF-TOKEN';
}

function proxyEnvironmentCookieName(): string | null {
  const target = process.env.REACT_APP_DEV_API_URL;
  if (!target) return null;

  try {
    const host = new URL(target).hostname;
    if (isKnowspreadEnvironment(host, 'dev')) return 'dev_XSRF-TOKEN';
    if (isKnowspreadEnvironment(host, 'staging')) return 'staging_XSRF-TOKEN';
  } catch {
    // A malformed override must not break the lookup for everyone else.
  }

  return null;
}

function isKnowspreadEnvironment(hostname: string, environment: string): boolean {
  const environmentHost = `${environment}.knowspread.com`;
  return hostname === environmentHost || hostname.endsWith(`.${environmentHost}`);
}

export function xsrfTokenMiddleware(): Middleware {
  return {
    pre: (context) => {
      const xsrfCookieName = getXsrfCookieName();
      const xsrfToken = document.cookie
        ?.split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith(`${xsrfCookieName}=`))
        ?.split('=')[1];

      const headersAddon = xsrfToken
        ? {
            [XSRF_TOKEN_HEADER]: xsrfToken,
          }
        : null;

      return Promise.resolve({
        init: {
          ...context.init,
          headers: {
            ...headersAddon,
            ...context.init.headers,
          },
        },
        url: context.url,
      });
    },
  };
}
